/*
 * IMPORTANT: DO NOT filter projects by ProjectStage.THUMBNAIL
 * 
 * This component should filter by project.thumbnail instead of project.stage
 * because we want to show ANY project that has a thumbnail image generated,
 * regardless of what stage the project is in. Projects can have thumbnails
 * generated at different stages of the workflow.
 * 
 * ❌ DON'T: project.stage === ProjectStage.THUMBNAIL
 * ✅ DO: project.thumbnail && project.thumbnail.trim() !== ''
 */

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from '@mantine/core';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import styles from './ThumbnailLanding.module.css';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';
import { 
  fetchProjects, 
  Project, 
  deleteProject, 
  duplicateProject,
  ProjectStage
} from '../../services/projectService';
import { LibraryItem } from '../../types';
import { 
  generateThumbnailFromDescription,
  uploadExtractedFrameImage,
  fetchProjectsWithVideos
} from '../../services/thumbnailGenerationApi';
import { useThumbnailGeneration } from './hooks/useThumbnailGeneration';
import { updateProjectCharacterRegistry } from '../../services/characterRegistryService';
import { extractCharacterIdFromPrompt, isCharacterReferencePrompt } from '../../utils/character-detection';
import ProcessingDashboard from '../ProcessingDashboard/ProcessingDashboard';
import LibraryImageModal from '../LibraryImageModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUpload, 
  faVideo, 
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faImage,
  faFileImage,
  faFilm
} from '@fortawesome/free-solid-svg-icons';
import AIImageModalFresh from '../Modal/AIImageModalFresh';
import VideoFrameSelectionModal from '../Modal/VideoFrameSelectionModal';

const ThumbnailLanding: React.FC = () => {
  const { t } = useTranslation('thumbnailLanding');
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  const [initialPromptText, setInitialPromptText] = useState(''); // For URL-based prompts
  const [isAIImageModalOpen, setIsAIImageModalOpen] = useState(false);
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false); // New loading overlay state
  const imageWasSelectedRef = useRef(false); // Use ref for immediate access
  const [isVideoFrameModalOpen, setIsVideoFrameModalOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [urlProjectId, setUrlProjectId] = useState<string | null>(null); // Project ID from URL params
  const [projectsWithVideos, setProjectsWithVideos] = useState<Project[]>([]);
  const [isLoadingVideoProjects, setIsLoadingVideoProjects] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [characterImageUrl, setCharacterImageUrl] = useState<string | null>(null); // Store character image URL
  const hasProcessedPrompt = useRef(false); // Prevent duplicate notifications using ref
  
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const projectDropdownRef = useRef<HTMLDivElement>(null);

  // Handle incoming prompt parameters
  useEffect(() => {
    const initialPrompt = searchParams.get('initial-prompt');
    const projectId = searchParams.get('project-id');
    const characterId = searchParams.get('character-id');
    const characterName = searchParams.get('character-name');
    const charUrl = searchParams.get('charUrl'); // 🎭 Character image URL
    
    // Only run if we have an initial prompt and haven't processed it yet
    if (initialPrompt && !hasProcessedPrompt.current) {
      hasProcessedPrompt.current = true; // Set immediately to prevent duplicates
      
      // Store project ID from URL params
      if (projectId) {
        setUrlProjectId(projectId);
        console.log('🔗 Project ID from URL:', projectId);
        console.log('🔗 Setting urlProjectId state to:', projectId);
      } else {
        console.log('❌ No project-id found in URL parameters');
        setUrlProjectId(null);
      }
      
      // 🎭 DEBUG: Display charUrl in console
      console.log('=================');
      console.log('🎭 ThumbnailLanding - Raw URL:', window.location.href);
      console.log('🎭 ThumbnailLanding - Raw search:', window.location.search);
      console.log('🎭 ThumbnailLanding - charUrl parameter:', charUrl);
      console.log('🎭 ThumbnailLanding - All URL params:');
      for (const [key, value] of searchParams.entries()) {
        console.log(`  ${key}: ${value}`);
      }
      console.log('=================');
      
      // 🐛 DEBUG: Log character parameters
      console.log('🎭 ThumbnailLanding - Character parameters:', {
        characterId,
        characterName,
        charUrl,
        hasCharacterId: !!characterId,
        hasCharacterName: !!characterName,
        hasCharUrl: !!charUrl,
        allSearchParams: Object.fromEntries(searchParams.entries())
      });
      
      // 🎭 If character image URL is provided, store it for use in image generation
      if (charUrl) {
        console.log('🎭 Character image URL provided, storing for image-to-image generation:', charUrl);
        setCharacterImageUrl(charUrl);
      }
      
      // Store the initial prompt for the modal
      setInitialPromptText(initialPrompt);
      
      // Show loading overlay immediately
      setIsLoadingModal(true);
      
      // Show notification about auto-populated prompt (only once)
      const isCharacter = characterId && characterName;
      // showNotification({
      //   title: isCharacter ? 'Character Prompt Loaded' : 'Prompt Loaded',
      //   message: isCharacter 
      //     ? `Preparing AI generator for ${characterName}...`
      //     : 'Preparing AI image generation...',
      //   type: 'info'
      // });

      // Auto-open AI Image Modal for immediate generation
      setTimeout(() => {
        setIsLoadingModal(false); // Hide loading overlay
        imageWasSelectedRef.current = false; // Reset flag when opening modal
        setIsAIImageModalOpen(true);
      }, 1000);
    }

    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, []); // Empty dependency array since we handle the check internally with ref
  
  // Use the thumbnail generation hook
  const {
    isLoading,
    status,
    currentStep,
    progress,
    error: generationError,
    generateFromDescription,
    reset,
    updateState
  } = useThumbnailGeneration();
  // Fetch projects when component mounts - filter by THUMBNAIL stage
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const loadProjects = async () => {
      try {
        setIsProjectsLoading(true);
        setError(null);
        
        timeoutId = setTimeout(() => {
          if (isMounted && isProjectsLoading) {
            setIsProjectsLoading(false);
            setError('Request timed out. Please try again.');
            setProjects([]);
          }
        }, 10000);
        
        console.log('Starting to fetch projects...');
        const response = await fetchProjects(1, 1000);
        console.log('Projects fetched successfully:', response);
        
        if (!isMounted) return;
        
        if (response && 'projects' in response) {
          // Filter projects to show only those with thumbnail
          const thumbnailProjects = (response.projects || []).filter(
            project => project.thumbnail && project.thumbnail.trim() !== ''
          );
          setProjects(thumbnailProjects);
        } else {
          setProjects([]);
          console.warn('API returned unexpected data format');
        }
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again later.');
        showNotification({
          title: 'Error',
          message: 'Failed to load projects',
          type: 'error'
        });
        setProjects([]);
      } finally {
        if (isMounted) {
          clearTimeout(timeoutId);
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();
    
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array - only run once on mount

  // Load projects with videos for the dropdown
  useEffect(() => {
    loadProjectsWithVideos();
  }, []); // Empty dependency array - only run once on mount

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setIsProjectModalOpen(false);
      }
    };

    if (isProjectModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProjectModalOpen]);

  // Separate function to load projects (for refresh functionality)
  const loadProjectsWithVideos = async () => {
    try {
      setIsLoadingVideoProjects(true);
      console.log('Starting to load projects with videos...');
      const projects = await fetchProjectsWithVideos();
      console.log('Loaded projects with videos:', projects);
      setProjectsWithVideos(projects);
    } catch (error) {
      console.error('Error fetching projects with videos:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load projects with videos',
        type: 'error'
      });
    } finally {
      setIsLoadingVideoProjects(false);
    }
  };

  // Handle AI Image modal - use the same API as the main Generate button
  // Handle closing the AI Image Modal
  const handleCloseAIImageModal = () => {
    console.log('🚪 MODAL CLOSING - imageWasSelectedRef.current:', imageWasSelectedRef.current);
    setIsAIImageModalOpen(false);
    setIsLoadingModal(false); // Ensure loading overlay is hidden when modal closes
    hasProcessedPrompt.current = false; // Reset prompt processing flag for next time
    
    // If an image was successfully selected, don't do cancel navigation
    if (imageWasSelectedRef.current) {
      console.log('✅ MODAL CLOSING AFTER IMAGE SELECTION - No cancel navigation needed');
      imageWasSelectedRef.current = false; // Reset flag
      return;
    }
    
    // Only do cancel navigation if modal was closed without selecting an image
    console.log('🚫 MODAL CLOSED WITHOUT SELECTION - Proceeding with cancel navigation');
    
    // Check URL parameters to determine return destination
    const sourceProjectId = searchParams.get('project-id');
    const cancelTo = searchParams.get('cancel-to');
    const mode = searchParams.get('mode');
    
    // Handle thumbnail modal cancellation (new priority)
    if (mode === 'thumbnail' && cancelTo && sourceProjectId) {
      console.log('🚫 THUMBNAIL MODAL CANCEL - Returning to:', cancelTo);
      navigate(`/${cancelTo}?project-id=${sourceProjectId}`);
      return;
    }
    
    // Handle prompt generator cancellation (existing logic)
    if (sourceProjectId) {
      // Navigate back to the prompt generator project
      navigate(`/prompt-generator/${sourceProjectId}`);
    }
    // If no source project, just stay on the current page (normal behavior)
  };

  const handleAIImageGenerated = async (imageUrl: string, prompt: string) => {
    console.log('🖼️ IMAGE SELECTED - Starting handleAIImageGenerated');
    console.log('📸 Selected image URL:', imageUrl);
    console.log('📝 Prompt used:', prompt);
    
    // Mark that image was successfully selected (use ref for immediate access)
    imageWasSelectedRef.current = true;
    console.log('🏷️ Set imageWasSelectedRef.current = true');
    
    // Check URL parameters to determine workflow
    const sourceProjectId = searchParams.get('project-id');
    const returnTo = searchParams.get('return-to');
    const mode = searchParams.get('mode');
    const nextPath = searchParams.get('next_path');
    
    // Determine workflow type
    const isFromThumbnailModal = mode === 'thumbnail' && returnTo === 'thumbnail-editor';
    const isFromPromptGenerator = !!sourceProjectId && !isFromThumbnailModal;
    
    // 🎭 Check if character image URL was provided for preloading
    const charUrl = searchParams.get('charUrl');
    
    console.log('🔍 Navigation context:', {
      sourceProjectId,
      returnTo,
      mode,
      nextPath,
      isFromThumbnailModal,
      isFromPromptGenerator,
      charUrl,
      searchParams: Object.fromEntries(searchParams.entries())
    });
    
    // Handle thumbnail modal workflow FIRST (new priority)
    if (isFromThumbnailModal && sourceProjectId) {
      console.log('🎯 THUMBNAIL MODAL WORKFLOW - Navigating to thumbnail editor');
      
      // Show context-aware loading message
      updateState({
        isLoading: true,
        status: 'processing',
        currentStep: 'Loading thumbnail editor with your image...',
        progress: 90
      });
      
      setTimeout(() => {
        navigate(`/thumbnail-editor/${sourceProjectId}?baseImage=${encodeURIComponent(imageUrl)}`);
        // showNotification({
        //   title: 'Opening Thumbnail Editor',
        //   message: 'AI-generated image loaded for editing',
        //   type: 'success'
        // });
      }, 800);
      
      return; // Exit early for thumbnail modal workflow
    }
    
    // Handle prompt generator workflow (existing logic)
    if (isFromPromptGenerator) {
      console.log('🔍 Search params:', Object.fromEntries(searchParams.entries()));
    }
    
    if (isFromPromptGenerator) {
      console.log('🎯 PROMPT GENERATOR WORKFLOW - Navigating to editor');
      
      // Check for character-related parameters
      const characterId = searchParams.get('character-id');
      const promptId = searchParams.get('prompt-id');
      
      console.log('🎭 Character parameters:', { characterId, promptId });
      
      // Update character registry if this is a character reference image
      if (characterId && promptId && sourceProjectId) {
        try {
          console.log('🔄 Updating character registry...');
          
          // Update loading state with context-aware message
          reset(); // Clear any existing state
          updateState({
            isLoading: true,
            status: 'processing',
            currentStep: 'Saving character and redirecting to prompts...',
            progress: 75
          });
          
          await updateProjectCharacterRegistry(sourceProjectId, characterId, imageUrl, promptId);
          console.log(`✅ Updated character registry for ${characterId} in project ${sourceProjectId}`);
          
          // showNotification({
          //   title: 'Character Updated!',
          //   message: `Character image saved to project registry`,
          //   type: 'success'
          // });
        } catch (error) {
          console.error('❌ Failed to update character registry:', error);
          showNotification({
            title: 'Warning',
            message: 'Failed to save character to registry, but continuing...',
            type: 'warning'
          });
        }
      }
      
      // Determine redirect destination based on whether this is a character workflow
      if (promptId) {
        // Character generation workflow - redirect back to prompt generator
        updateState({
          currentStep: 'Redirecting to Prompt Generator...',
          progress: 100
        });
        
        setTimeout(() => {
          navigate(`/prompt-generator/${sourceProjectId}`);
          // showNotification({
          //   title: 'Character Updated!',
          //   message: 'Character image saved and returning to prompts',
          //   type: 'success'
          // });
        }, 800);
      } else {
        // Regular thumbnail generation workflow - go to thumbnail editor
        const baseImageUrl = charUrl || imageUrl;
        console.log('🖼️ Using base image URL:', baseImageUrl);
        
        updateState({
          currentStep: 'Loading thumbnail editor with your image...',
          progress: 100
        });
        
        setTimeout(() => {
          navigate(`/thumbnail-editor/${sourceProjectId}?baseImage=${encodeURIComponent(baseImageUrl)}`);
          // showNotification({
          //   title: 'Opening Thumbnail Editor',
          //   message: 'Generated image loaded for editing',
          //   type: 'success'
          // });
        }, 800);
      }
      
      return; // Exit early for prompt generator workflow
    }
    
    // Default case: Regular thumbnail generation workflow
    // This happens when user comes directly to thumbnail generator and generates an image
    console.log('📋 REGULAR THUMBNAIL WORKFLOW - Creating new thumbnail project');
    
    // Show loading state
    updateState({
      isLoading: true,
      status: 'processing',
      currentStep: 'Creating new thumbnail project...',
      progress: 90
    });
    
    // Create a new project by calling the thumbnail generation API with existing image URL
    try {
      const payload = {
        method: 'existing_image',
        existingImageUrl: imageUrl,
        title: `AI Thumbnail - ${new Date().toLocaleDateString()}`,
        width: 1536,
        height: 864,
        aspectRatio: '16:9'
      };

      console.log('🔍 DEBUG: Sending payload to API:', JSON.stringify(payload, null, 2));

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('🔍 DEBUG: Sending headers:', headers);
      
      const response = await fetch(getApiUrl('/thumbnail/generate'), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      console.log('🔍 DEBUG: Response status:', response.status);
      console.log('🔍 DEBUG: Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔍 DEBUG: Error response text:', errorText);
        throw new Error(`Failed to create project: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('New thumbnail project created:', result);

      if (result.success && result.data) {
        setTimeout(() => {
          navigate(`/thumbnail-editor/${result.data.projectId}?baseImage=${encodeURIComponent(imageUrl)}`);
          // showNotification({
          //   title: 'Thumbnail Project Created',
          //   message: 'AI-generated image loaded in new project',
          //   type: 'success'
          // });
        }, 800);
      } else {
        throw new Error(result.message || 'Failed to create project - no data returned');
      }
      
    } catch (error) {
      console.error('❌ Failed to create thumbnail project:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create thumbnail project',
        type: 'error'
      });
      
      // Reset loading state on error
      reset();
    }
  };

  // Handle library image selection
  const handleLibraryImageSelected = (item: LibraryItem) => {
    console.log('📚 LIBRARY IMAGE SELECTED:', item);
    
    // Check URL parameters to determine workflow
    const sourceProjectId = searchParams.get('project-id');
    const returnTo = searchParams.get('return-to');
    const mode = searchParams.get('mode');
    
    // Determine workflow type
    const isFromThumbnailModal = mode === 'thumbnail' && returnTo === 'thumbnail-editor';
    
    if (isFromThumbnailModal && sourceProjectId) {
      console.log('🎯 LIBRARY → THUMBNAIL MODAL WORKFLOW - Navigating to thumbnail editor');
      
      navigate(`/thumbnail-editor/${sourceProjectId}?baseImage=${encodeURIComponent(item.url)}`);
      // showNotification({
      //   title: 'Opening Thumbnail Editor',
      //   message: `Library image "${item.name}" loaded for editing`,
      //   type: 'success'
      // });
      
      return;
    }
    
    // Default behavior for other workflows
    console.log('📋 LIBRARY → REGULAR WORKFLOW');
    if (sourceProjectId) {
      navigate(`/thumbnail-editor/${sourceProjectId}?baseImage=${encodeURIComponent(item.url)}`);
    }
  };

  // Handle image upload for direct editing
  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification({
        title: 'Error',
        message: 'Please select a valid image file (PNG, JPG, GIF, etc.)',
        type: 'error'
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showNotification({
        title: 'Error',
        message: 'Image file is too large. Please select a file smaller than 10MB.',
        type: 'error'
      });
      return;
    }

    console.log('Starting image upload:', file.name, 'Size:', Math.round(file.size / 1024), 'KB');

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('method', 'upload');
      formData.append('title', `Uploaded Image: ${file.name}`);
      formData.append('image', file);
      formData.append('width', '1536');
      formData.append('height', '864');

      // Show upload progress notification
      // showNotification({
      //   title: 'Uploading',
      //   message: 'Uploading your image...',
      //   type: 'info'
      // });

      // Call the upload API
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiUrl('/thumbnail/generate'), {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Image upload result:', result);

      if (result.success && result.data) {
        // showNotification({
        //   title: 'Success',
        //   message: 'Image uploaded successfully!',
        //   type: 'success'
        // });

        console.log('Navigating to editor with uploaded image:', {
          projectId: result.data.projectId,
          s3Url: result.data.s3Url
        });

        // Navigate to thumbnail editor with the uploaded image
        navigate(`/thumbnail-editor/${result.data.projectId}?baseImage=${encodeURIComponent(result.data.s3Url)}`);
      } else {
        throw new Error(result.message || 'Upload failed - no data returned');
      }

    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        type: 'error'
      });
    } finally {
      // Reset the file input
      event.target.value = '';
    }
  };
  // Handle video upload for frame extraction
  const handleVideoUpload = () => {
    videoInputRef.current?.click();
  };

  const handleVideoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Store the selected file and open the video frame selection modal
    setSelectedVideoFile(file);
    setIsVideoFrameModalOpen(true);
    
    // Reset the file input
    event.target.value = '';
  };

  // Handle frame selection from video extractor
  const handleFrameSelected = async (imageData: string, timestamp: number) => {
    try {
      // Upload the extracted frame image and create project
      const result = await uploadExtractedFrameImage(imageData, {
        title: `Video Frame at ${Math.floor(timestamp / 60)}:${Math.floor(timestamp % 60).toString().padStart(2, '0')}`,
        timestamp: timestamp
      });
      
      if (result) {
        // showNotification({
        //   title: 'Frame Selected',
        //   message: 'Frame extracted successfully! Redirecting to thumbnail editor.',
        //   type: 'success'
        // });
        
        // Navigate to thumbnail editor with the extracted frame
        navigate(`/thumbnail-editor/${result.projectId}?baseImage=${encodeURIComponent(result.s3Url)}`);
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to process extracted frame',
        type: 'error'
      });
    }
  };

  // Handle video upload from existing project
  const handleVideoFromProject = async () => {
    console.log('🎬 handleVideoFromProject called');
    console.log('📋 Selected Project ID:', selectedProjectId);
    
    if (!selectedProjectId) {
      console.log('❌ No project selected');
      showNotification({
        title: 'Error',
        message: 'Please select a project first',
        type: 'error'
      });
      return;
    }

    try {
      // Get the project's video URL directly from the projects we already loaded
      const selectedProject = projectsWithVideos.find(p => p.id === selectedProjectId);
      
      if (!selectedProject) {
        throw new Error('Selected project not found');
      }

      const videoUrl = selectedProject.originalVideoUrl || selectedProject.generatedVideoUrl;
      
      if (!videoUrl) {
        throw new Error('No video URL found for this project');
      }
      
      console.log('🔗 Got project video URL:', videoUrl);
      
      // Convert S3 URL to a "virtual file" that looks like an uploaded file
      // but contains the S3 URL directly for the modal to use
      const virtualVideoFile = {
        name: `${selectedProject?.title || 'Project Video'}.mp4`,
        type: 'video/mp4',
        size: 0, // We don't know the actual size, but modal doesn't really need it
        // Add custom properties to identify this as a project video
        isProjectVideo: true,
        projectId: selectedProjectId,
        projectTitle: selectedProject?.title || 'Project Video',
        s3Url: videoUrl, // The actual S3 URL to use
        // Mock the File interface enough for the modal to work
        lastModified: Date.now(),
        webkitRelativePath: ''
      };
      
      console.log('📦 Created virtual video file:', virtualVideoFile);
      console.log('🚀 Opening video frame modal with S3 URL...');
      
      // Open the existing VideoFrameSelectionModal with our virtual file
      setIsVideoFrameModalOpen(true);
      setSelectedVideoFile(virtualVideoFile as any);
        
    } catch (error) {
      console.error('❌ Failed to get project video URL:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load project video. Please try again.',
        type: 'error'
      });
    }
  };

  // Handle project actions
  const handleProjectAction = async (action: string, projectId: string) => {
    setActiveActionDropdown(null);
    
    switch (action) {
      case 'delete':
        try {
          await deleteProject(projectId);
          setProjects(projects.filter(p => p.id !== projectId));
          showNotification({
            title: 'Success',
            message: 'Project deleted successfully',
            type: 'success'
          });
        } catch (error) {
          showNotification({
            title: 'Error',
            message: 'Failed to delete project',
            type: 'error'
          });
        }
        break;      case 'duplicate':
        try {
          const duplicatedProject = await duplicateProject(projectId);
          setProjects([duplicatedProject, ...projects]);
          showNotification({
            title: 'Success',
            message: 'Project duplicated successfully',
            type: 'success'
          });
        } catch (error) {
          showNotification({
            title: 'Error',
            message: 'Failed to duplicate project',
            type: 'error'
          });
        }
        break;
      case 'edit':
        navigate(`/thumbnail-editor/${projectId}`);
        break;
    }
  };

  // Filter projects based on search term and selected option
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOption = selectedOption === 'all' || 
      (selectedOption === 'recent' && new Date(project.date) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    return matchesSearch && matchesOption;
  });

  if (isLoading) {
    return (
      <ProcessingDashboard 
        status={status === 'idle' ? 'processing' : status}
        progress={progress}
        message={currentStep}
        error={generationError || undefined}
      />
    );
  }

  return (
    <div className={styles.container}>
      <SEO
        title={seoConfig.thumbnailGenerator.title}
        description={seoConfig.thumbnailGenerator.description}
        keywords={seoConfig.thumbnailGenerator.keywords}
        url="https://kingdomstudio.pro/thumbnail-landing"
      />
      <Navigation />
      
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('thumbnailLanding.header.title')}</h1>
          <p className={styles.subtitle}>{t('thumbnailLanding.header.subtitle')}</p>
        </div>

        {/* Card Options Section */}
        <div className={styles.cardOptions} style={{ overflow: 'visible' }}>
          {/* Generate Thumbnail */}
          <div className={styles.optionCard}>
            <div className={styles.cardIcon}>
              <FontAwesomeIcon icon={faImage} />
            </div>
            <h3>{t('thumbnailLanding.cards.generate.title')}</h3>
            <p>{t('thumbnailLanding.cards.generate.description')}</p>
            <Button 
              onClick={() => {
                // Show loading overlay before opening modal
                setIsLoadingModal(true);
                setTimeout(() => {
                  setIsLoadingModal(false);
                  imageWasSelectedRef.current = false; // Reset flag when opening modal
                  setIsAIImageModalOpen(true);
                }, 500); // Shorter delay for manual action
              }}
              disabled={isLoading}
              className={styles.actionButton}
            >
              <FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />
              {t('thumbnailLanding.cards.generate.button')}
            </Button>
          </div>

          {/* Image Upload */}
          <div className={styles.optionCard}>
            <div className={styles.cardIcon}>
              <FontAwesomeIcon icon={faFileImage} />
            </div>
            <h3>{t('thumbnailLanding.cards.upload.title')}</h3>
            <p>{t('thumbnailLanding.cards.upload.description')}</p>
            <Button 
              onClick={handleImageUpload}
              disabled={isLoading}
              className={styles.actionButton}
            >
              <FontAwesomeIcon icon={faUpload} className={styles.buttonIcon} />
              {t('thumbnailLanding.cards.upload.button')}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>

          {/* Load from Library */}
          {/* TODO: Library is project-specific, not needed on landing page
          <div className={styles.optionCard}>
            <div className={styles.cardIcon}>
              <FontAwesomeIcon icon={faImage} />
            </div>
            <h3>Load from Library</h3>
            <p>Choose an existing image from your project library</p>
            <Button 
              onClick={() => setIsLibraryModalOpen(true)}
              disabled={isLoading}
              className={styles.actionButton}
            >
              <FontAwesomeIcon icon={faImage} className={styles.buttonIcon} />
              Browse Library
            </Button>
          </div>
          */}

          {/* Video Frame Extraction */}
          <div className={styles.optionCard}>
            <div className={styles.cardIcon}>
              <FontAwesomeIcon icon={faFilm} />
            </div>
            <h3>{t('thumbnailLanding.cards.extractVideo.title')}</h3>
            <p>{t('thumbnailLanding.cards.extractVideo.description')}</p>
            <Button 
              onClick={handleVideoUpload}
              disabled={isLoading}
              className={styles.actionButton}
            >
              <FontAwesomeIcon icon={faVideo} className={styles.buttonIcon} />
              {t('thumbnailLanding.cards.extractVideo.button')}
            </Button>
            <input
              type="file"
              ref={videoInputRef}
              onChange={handleVideoFileChange}
              accept=".mp4,.avi,.mov,.wmv,.flv,.webm,.mkv,video/mp4,video/avi,video/mov,video/quicktime,video/x-quicktime,video/x-ms-wmv,video/x-flv,video/webm,video/x-matroska"
              style={{ display: 'none' }}
            />
          </div>

          {/* Extract from Project Video */}
          {/* TODO: Project video extraction is project-specific, not needed on landing page */}
          {/* <div className={styles.optionCard} style={{ overflow: 'visible' }}>
            <div className={styles.cardIcon}>
              <FontAwesomeIcon icon={faVideo} />
            </div>
            <h3>Extract from Project</h3>
            <p>Extract a frame from an existing project's video</p>
            
            {isLoadingVideoProjects ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '20px',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <Loader size="sm" />
                <span style={{ color: '#8b5cf6', fontSize: '14px' }}>
                  Loading projects with videos...
                </span>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    color: '#8b5cf6',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '12px'
                  }}>
                    Select Project
                  </div>
                  
                  {projectsWithVideos.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#999'
                    }}>
                      <div style={{ marginBottom: '8px' }}>No completed projects with videos found</div>
                      <div style={{ fontSize: '12px', fontStyle: 'italic' }}>
                        Complete a video project first, then refresh to see it here
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '15px' }}>
                      
                      <div
                        onClick={() => setIsProjectModalOpen(true)}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease',
                          minHeight: '48px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {selectedProjectId ? (
                            (() => {
                              const selectedProject = projectsWithVideos.find(p => p.id === selectedProjectId);
                              const videoUrl = selectedProject?.originalVideoUrl || selectedProject?.generatedVideoUrl;
                              return (
                                <>
                                  <div style={{
                                    width: '32px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    backgroundColor: '#2a2a2a',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}>
                                    {videoUrl ? (
                                      <video
                                        src={videoUrl}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                        muted
                                        playsInline
                                        preload="metadata"
                                      />
                                    ) : (
                                      <div style={{ color: '#8b5cf6', fontSize: '12px' }}>🎬</div>
                                    )}
                                  </div>
                                  <div>
                                    <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                                      {selectedProject?.title}
                                    </div>
                                  </div>
                                </>
                              );
                            })()
                          ) : (
                            <div style={{ color: '#999', fontSize: '14px' }}>
                              Click to select a project with video
                            </div>
                          )}
                        </div>
                        <div style={{ color: '#8b5cf6', fontSize: '14px' }}>
                          📋
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                  <Button 
                    onClick={handleVideoFromProject}
                    disabled={isLoading || !selectedProjectId || projectsWithVideos.length === 0}
                    className={styles.actionButton}
                    style={{ flex: 1 }}
                  >
                    <FontAwesomeIcon icon={faVideo} className={styles.buttonIcon} />
                    Use Project Video
                  </Button>
                </div>
              </>
            )}
          </div> */}
          
        </div>
        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.projectsHeader}>
            <h2>{t('thumbnailLanding.projects.title')}</h2>
            <div className={styles.projectsControls}>
              <div className={styles.filterButtons}>
                <Button
                  variant="outline"
                  onClick={() => setSelectedOption('all')}
                  size="sm"
                  className={selectedOption === 'all' ? styles.filterButtonActive : styles.filterButton}
                >
                  {t('thumbnailLanding.projects.filters.all')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedOption('recent')}
                  size="sm"
                  className={selectedOption === 'recent' ? styles.filterButtonActive : styles.filterButton}
                >
                  {t('thumbnailLanding.projects.filters.recent')}
                </Button>
              </div>
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  placeholder={t("thumbnailLanding.projects.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </div>

          <div className={styles.projectsGrid}>
            {isProjectsLoading ? (
              <div className={styles.loadingContainer}>
                <Loader size="lg" />
                <p>{t('thumbnailLanding.projects.loading')}</p>
              </div>
            ) : error ? (
              <div className={styles.errorContainer}>
                <p className={styles.errorMessage}>{error}</p>
                <Button onClick={() => window.location.reload()}>
                  {t('thumbnailLanding.projects.tryAgain')}
                </Button>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faImage} className={styles.emptyIcon} />
                <h3>No thumbnail projects found</h3>
                <p>Create your first AI-generated thumbnail using the options above!</p>
              </div>
            ) : (
              filteredProjects.map((project) => (
                <div 
                  key={project.id} 
                  className={styles.projectCard}
                  onClick={() => navigate(`/thumbnail-editor/${project.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={styles.projectThumbnail}>
                    {project.thumbnail ? (
                      <img src={project.thumbnail} alt={project.title} />
                    ) : (
                      <div className={styles.placeholderThumbnail}>
                        <FontAwesomeIcon icon={faImage} />
                      </div>
                    )}
                  </div>                  <div className={styles.projectInfo}>
                    <h4 className={styles.projectTitle}>{project.title}</h4>
                    <div className={styles.projectMeta}>
                      <span className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {new Date(project.date).toLocaleDateString()}
                      </span>
                      <div className={styles.projectActions}>
                        <button
                          className={styles.actionsButton}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            setActiveActionDropdown(
                              activeActionDropdown === project.id ? null : project.id
                            );
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                        {activeActionDropdown === project.id && (
                          <div className={styles.actionsDropdown}>
                            <button onClick={() => handleProjectAction('edit', project.id)}>
                              <FontAwesomeIcon icon={faImage} />
                              Edit
                            </button>
                            <button onClick={() => handleProjectAction('duplicate', project.id)}>
                              <FontAwesomeIcon icon={faDownload} />
                              Duplicate
                            </button>
                            <button 
                              onClick={() => handleProjectAction('delete', project.id)}
                              className={styles.deleteAction}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Loading Overlay - appears when preparing AI Image Generator */}
      {isLoadingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99998,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            color: 'white',
            textAlign: 'center'
          }}>
            <Loader size="xl" color="#8b5cf6" />
            <div style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#8b5cf6'
            }}>
              Preparing AI Image Generator...
            </div>
            <div style={{
              fontSize: '14px',
              color: '#ccc',
              opacity: 1
            }}>
              Loading your prompt and character references
            </div>
          </div>
        </div>
      )}

      {/* AI Image Modal */}
      <AIImageModalFresh
        isOpen={isAIImageModalOpen}
        onClose={handleCloseAIImageModal}
        onImageGenerated={handleAIImageGenerated}
        projectId={urlProjectId || undefined}
        initialTab="image"
        initialPrompt={initialPromptText}
        disableVideoTab={true}
        disablePromptsTab={true}
        initialImageUrl={characterImageUrl}
      />

      {/* Library Image Selection Modal */}
      <LibraryImageModal
        isOpen={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        onImageSelected={handleLibraryImageSelected}
        projectId={urlProjectId || ''}
        title="Select Image from Library"
        subtitle="Choose an existing image to use as your thumbnail"
        selectButtonText="Use This Image"
      />

      {/* Video Frame Selection Modal */}
      <VideoFrameSelectionModal
        isOpen={isVideoFrameModalOpen}
        onClose={() => {
          setIsVideoFrameModalOpen(false);
          setSelectedVideoFile(null);
        }}
        onFrameSelected={handleFrameSelected}
        videoFile={selectedVideoFile}
        maxFileSize={2 * 1024 * 1024 * 1024} // 2GB
      />

      {/* Project Selection Modal */}
      {isProjectModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div
            ref={projectDropdownRef}
            style={{
              backgroundColor: '#2a2a2a',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{
                color: 'white',
                margin: 0,
                fontSize: '18px',
                fontWeight: '600'
              }}>
                Select Project Video
              </h3>
              <button
                onClick={() => setIsProjectModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Search Input */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <input
                type="text"
                placeholder={t("thumbnailLanding.projects.search")}
                value={projectSearchTerm}
                onChange={(e) => setProjectSearchTerm(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#8b5cf6';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              />
            </div>

            {/* Projects List */}
            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px 0' }}>
              {projectsWithVideos
                .filter(project => 
                  project.title.toLowerCase().includes(projectSearchTerm.toLowerCase())
                )
                .sort((a, b) => new Date(b.date || b.createdAt || 0).getTime() - new Date(a.date || a.createdAt || 0).getTime()) // Sort by date, newest first
                .map(project => {
                  const videoUrl = project.originalVideoUrl || project.generatedVideoUrl;
                  const isSelected = selectedProjectId === project.id;
                  return (
                    <div
                      key={project.id}
                      onClick={() => {
                        setSelectedProjectId(project.id);
                        setIsProjectModalOpen(false);
                        setProjectSearchTerm('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '16px 20px',
                        backgroundColor: isSelected 
                          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
                          : 'transparent',
                        borderLeft: isSelected ? '4px solid #8b5cf6' : '4px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {/* Video Preview Thumbnail */}
                      <div style={{
                        width: '60px',
                        height: '40px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {videoUrl ? (
                          <video
                            src={videoUrl}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            muted
                            playsInline
                            preload="metadata"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                parent.innerHTML = '<div style="color: #8b5cf6; font-size: 18px;">🎬</div>';
                              }
                            }}
                          />
                        ) : (
                          <div style={{ color: '#8b5cf6', fontSize: '18px' }}>🎬</div>
                        )}
                      </div>

                      {/* Project Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: 'white',
                          fontSize: '16px',
                          fontWeight: '500',
                          marginBottom: '4px'
                        }}>
                          {project.title}
                        </div>
                        <div style={{
                          color: '#a0a0a0',
                          fontSize: '13px'
                        }}>
                          Status: {project.status ? `${project.status}` : 'Ready'}
                        </div>
                      </div>

                      {/* Selection Indicator */}
                      {isSelected && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
                
              {/* No results message */}
              {projectsWithVideos
                .filter(project => 
                  project.title.toLowerCase().includes(projectSearchTerm.toLowerCase())
                )
                .length === 0 && (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#999'
                }}>
                  <div style={{ fontSize: '18px', marginBottom: '8px' }}>🔍</div>
                  <div>No projects found matching "{projectSearchTerm}"</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThumbnailLanding;